import {useCallback, useEffect, useRef} from 'react';
import {Platform} from 'react-native';
import {clearCardReissueEsiaPending, isCardReissueEsiaPending} from '../../../shared/storage/cardReissueEsia';
import {hasEsiaOAuthParams} from '../../../shared/utils/esiaOAuthParams';
import {hideGlobalLoader, showGlobalLoader} from '../../../shared/utils/globalLoader';

interface Params {
  isFocused: boolean;
  isEsia: boolean;
  isFormModalOpen: boolean;
}

export const useCardReissueLoader = ({isFocused, isEsia, isFormModalOpen}: Params) => {
  const canShowLoaderRef = useRef(true);
  const wasFocusedRef = useRef(false);
  const wasEsiaRef = useRef(false);

  const hideAndLockGlobalLoader = useCallback(() => {
    canShowLoaderRef.current = false;
    hideGlobalLoader({resetBlockingCalls: true});
  }, []);

  const cancelEsia = useCallback(async () => {
    hideAndLockGlobalLoader();
    await clearCardReissueEsiaPending();
  }, [hideAndLockGlobalLoader]);

  const unlockGlobalLoader = useCallback(() => {
    canShowLoaderRef.current = true;
  }, []);

  useEffect(() => {
    const becameFocused = isFocused && !wasFocusedRef.current;
    wasFocusedRef.current = isFocused;
    const leftEsiaStep = wasEsiaRef.current && !isEsia;
    wasEsiaRef.current = isEsia;

    if (isFormModalOpen) {
      return;
    }

    if (Platform.OS !== 'web' && (!isFocused || isEsia)) {
      hideGlobalLoader();
      return;
    }

    if (!isFocused || !isCardReissueEsiaPending()) {
      return;
    }

    const waitingForEsia = isEsia || hasEsiaOAuthParams();
    if (waitingForEsia) {
      if (canShowLoaderRef.current) {
        showGlobalLoader();
      }
      return;
    }

    if (Platform.OS === 'web') {
      return;
    }

    if (becameFocused || leftEsiaStep) {
      void cancelEsia();
    }
  }, [cancelEsia, isEsia, isFocused, isFormModalOpen]);

  useEffect(() => {
    if (isFormModalOpen) {
      hideAndLockGlobalLoader();
    }
  }, [hideAndLockGlobalLoader, isFormModalOpen]);

  useEffect(() => () => hideGlobalLoader({resetBlockingCalls: true}), []);

  return {
    hideAndLockGlobalLoader,
    cancelEsia,
    unlockGlobalLoader,
    showGlobalLoader: showGlobalLoader,
  };
};
